import { libraryFoldersChangedEvent } from '@lib/events/eventBus';
import {
  FolderColorId,
  folderEntryPath,
  FolderPath,
  folderPathAncestry,
  folderPathDepth,
  folderPathName,
  isDescendantFolderPath,
  LibraryFolderEntry,
  parentFolderPath,
} from '@lib/library/folderPath';
import { ApiError } from '@lib/network/api';
import { signalWithTimeout } from '@lib/utils/upload-retry';
import { userStore } from '@stores/User';
import { Media } from '@typings/Media';

/**
 * The by-path folder routes of a library (`/library/{id}/files`, `/folders`, `/media/move`).
 *
 * Every folder write here answers with the library's whole folder list and publishes it on
 * `libraryFoldersChangedEvent` before returning, so whoever called — the library page, or the
 * upload pipeline filing a dropped directory — the open library sees the same tree.
 */

export type LibraryFilesListing = {
  folders: LibraryFolderEntry[];
  files: Media[];
};

/**
 * A deadline on each folder write, so one that never answers cannot hold up the library's later
 * writes queued behind it. Generous, since a delete takes the folder's media with it.
 */
const FOLDER_WRITE_TIMEOUT_MS = 60_000;

const pendingFolderWrites = new Map<string, Promise<unknown>>();

/**
 * Sends one folder write of a library once its previous one has settled, and publishes the tree
 * it answers with. Every write answers with the whole tree and the store keeps the last one it
 * hears, so two in flight at once — a dropped directory's folders being created while the user
 * drags a folder — could land out of order and put the older tree back. The backend serializes
 * them anyway: each is a read-modify-write of the one library document.
 */
const writeLibraryFolders = <T extends { folders: LibraryFolderEntry[] }>(
  libraryId: string,
  query: string,
  init: { method: 'POST' | 'PATCH' | 'DELETE'; body?: object },
): Promise<T> => {
  const previous = pendingFolderWrites.get(libraryId) ?? Promise.resolve();
  const answer = previous.then(async () => {
    const response = await userStore.apiWithAuth<T>(`/library/${libraryId}/folders${query}`, {
      ...init,
      signal: signalWithTimeout(undefined, FOLDER_WRITE_TIMEOUT_MS),
    });
    libraryFoldersChangedEvent.emit({ libraryId, folders: response.folders });
    return response;
  });
  // The queue only needs to know the write settled; its caller gets the failure from `answer`.
  const settled = answer.catch(() => undefined);
  pendingFolderWrites.set(libraryId, settled);
  void settled.then(() => {
    if (pendingFolderWrites.get(libraryId) === settled) pendingFolderWrites.delete(libraryId);
  });
  return answer;
};

/** The library's folders and every media in it. */
export const listLibraryFiles = (libraryId: string, signal?: AbortSignal) =>
  userStore.apiWithAuth<LibraryFilesListing>(`/library/${libraryId}/files`, { signal });

export const createLibraryFolder = async (
  libraryId: string,
  folder: { parent: FolderPath; name: string; color?: FolderColorId },
) => (await writeLibraryFolders(libraryId, '', { method: 'POST', body: folder })).folders;

/** Renames, moves or recolours a folder; everything under it, media included, follows. */
export const updateLibraryFolder = async (
  libraryId: string,
  path: FolderPath,
  updates: { name?: string; parent?: FolderPath; color?: FolderColorId | null },
) =>
  (await writeLibraryFolders(libraryId, '', { method: 'PATCH', body: { path, ...updates } }))
    .folders;

/** Deletes the folder, the folders under it, and every media in any of them. */
export const deleteLibraryFolder = (libraryId: string, path: FolderPath) =>
  writeLibraryFolders<{ folders: LibraryFolderEntry[]; deletedMediaCount: number }>(
    libraryId,
    `?${new URLSearchParams({ path })}`,
    { method: 'DELETE' },
  );

/** Files media in a folder: all of them, or — on any unknown folder or media — none. */
export const moveMediaToFolder = (libraryId: string, mediaIds: string[], folderPath: FolderPath) =>
  userStore.apiWithAuth(`/library/${libraryId}/media/move`, {
    method: 'POST',
    body: { mediaIds, folderPath },
  });

/**
 * Makes every folder in `paths` exist below `base`, creating the missing ones parent-first — the
 * tree a dropped directory brings with it. Nothing at or above `base` is created: the folder the
 * user dropped on is theirs to have, and one deleted meanwhile is not brought back — the files
 * meant for it fail instead. `existing` is the library's folder list as last read; a folder
 * created meanwhile by another tab comes back as `already_exists`, which is the outcome wanted.
 *
 * Sequential on purpose: each create is a read-modify-write of the one library document, so
 * parallel creates would only conflict with each other. The backend caps a library at 500
 * folders (`library.folders.too_many`), which bounds the loop.
 *
 * Returns the paths that could not be created. The caller files nothing in those — the ingest
 * routes refuse a folder the library does not hold — and says why. `isCancelled` is checked before
 * each create, so a cancelled upload stops making folders for files that will never arrive.
 */
export const ensureLibraryFolders = async (
  libraryId: string,
  existing: LibraryFolderEntry[],
  base: FolderPath,
  paths: FolderPath[],
  isCancelled: () => boolean,
): Promise<{ failed: { path: FolderPath; error: unknown }[] }> => {
  const present = new Set<FolderPath>(existing.map(folderEntryPath));
  const wanted = [...new Set(paths.flatMap(folderPathAncestry))]
    .filter((path) => isDescendantFolderPath(path, base) && !present.has(path))
    // Shallow first, so every parent exists by the time its children are created.
    .sort((a, b) => folderPathDepth(a) - folderPathDepth(b));

  const failed: { path: FolderPath; error: unknown }[] = [];
  for (const path of wanted) {
    if (isCancelled()) break;
    const parent = parentFolderPath(path);
    if (failed.some((f) => f.path === parent)) {
      failed.push({ path, error: failed.find((f) => f.path === parent)!.error });
      continue;
    }
    try {
      await createLibraryFolder(libraryId, { parent, name: folderPathName(path) });
    } catch (error) {
      if (error instanceof ApiError && error.code === LIBRARY_FOLDER_ERROR.alreadyExists) continue;
      failed.push({ path, error });
    }
  }
  return { failed };
};

/**
 * The folder routes' refusals, and what to tell the user about them. The API's `message` is a
 * diagnostic for us, so it never reaches the screen — the same split as `mediaCommentErrors.ts`.
 */
export const LIBRARY_FOLDER_ERROR = {
  notFound: 'library.folders.not_found',
  parentNotFound: 'library.folders.parent_not_found',
  alreadyExists: 'library.folders.already_exists',
  invalidPath: 'library.folders.invalid_path',
  rootNotAllowed: 'library.folders.root_not_allowed',
  invalidMove: 'library.folders.invalid_move',
  pathTooLong: 'library.folders.path_too_long',
  tooMany: 'library.folders.too_many',
  mediaNotFound: 'media.not_found',
  readOnly: 'library.readonly',
} as const;

/** One sentence explaining a failed folder operation, for the toast's description. */
export function describeLibraryFolderError(error: unknown): string {
  const code = error instanceof ApiError ? error.code : null;
  switch (code) {
    case LIBRARY_FOLDER_ERROR.alreadyExists:
      return 'There is already a folder with that name here. Pick another name.';
    case LIBRARY_FOLDER_ERROR.invalidPath:
      return 'Folder names can’t contain “/” or be “.” or “..”.';
    case LIBRARY_FOLDER_ERROR.pathTooLong:
      return 'That would nest folders too deeply. Try somewhere closer to the top.';
    case LIBRARY_FOLDER_ERROR.invalidMove:
      return 'A folder can’t be moved inside itself.';
    case LIBRARY_FOLDER_ERROR.tooMany:
      return 'This library holds as many folders as it can. Delete one to make room.';
    case LIBRARY_FOLDER_ERROR.notFound:
    case LIBRARY_FOLDER_ERROR.parentNotFound:
      return 'That folder no longer exists. It may have been moved or deleted.';
    case LIBRARY_FOLDER_ERROR.mediaNotFound:
      return 'Some of that media is no longer in this library, so nothing was moved.';
    case LIBRARY_FOLDER_ERROR.readOnly:
      return 'This library is read-only.';
    default:
      return 'Something went wrong. Please try again.';
  }
}
